import { useState, useCallback, useRef, useEffect } from "react";
import { LanguageCode, toLanguageCode } from "../models/enums/LanguageCode";
import { useLanguage } from "../i18n/LanguageContext";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import {
  useEntityTranslations,
  useEntityTranslationFlags,
  useTargetLanguages,
  useRegenerateTranslation,
} from "./useTranslations";

// Primary language code (matches backend config)
const PRIMARY_LANGUAGE = LanguageCode.Fr;

/**
 * UI state of a field in the DISPLAYED language, driving the permanent status chip:
 * - "source": the talent's input language — always hand-written, always editable, never
 *   hand-backable to the AI (settings-anchored: source = UserSettings.PreferredLanguage).
 * - "ai": the translation is AI-managed → read-only until explicitly unlocked (draft mode).
 * - "human": a fresh human-authored translation ("version personnalisée").
 * - "humanStale": a human-authored translation whose source text changed since.
 */
export type TranslatableFieldUiState = "source" | "ai" | "human" | "humanStale";

/** Meaningful content check shared by the AI-first gate (Tiptap's empty doc is "<p></p>"). */
const hasMeaningfulContent = (value: string | undefined): boolean =>
	!!value && value.trim() !== "" && value !== "<p></p>";

interface UseTranslatableFormOptions {
  entityType: string;
  entityId?: number;
  translatableFields: string[];
  initialValues: Record<string, string>;
  /**
   * The talent's working language (their source). Defaults to the UI language. For an internal
   * user editing a talent's profile, pass the TALENT's PreferredLanguage so the source is the
   * talent's, not the internal user's.
   */
  sourceLanguage?: LanguageCode;
  /**
   * Gate for the translation queries (default true). Pass false when the translatable UI is not
   * rendered for the current user (e.g. CompanyForm in client onboarding): the API enforces
   * ownership on these reads, so background queries the user has no access to would 403 — and
   * they would be wasted requests anyway.
   */
  enabled?: boolean;
}

interface UseTranslatableFormReturn {
  editingLanguage: LanguageCode;
  switchLanguage: (lang: LanguageCode) => void;
  getFieldValue: (field: string) => string;
  setFieldValue: (field: string, value: string) => void;
  getModifiedLanguages: () => { languageCode: LanguageCode; fields: Record<string, string> }[];
  getPrimaryValues: () => Record<string, string>;
  /**
   * Current values of ONE language (server-loaded + session edits). Used by save flows to fill the
   * UNTOUCHED fields of a per-language payload: sending "" for them would read as a per-language
   * clear server-side (400 on required fields when no other human cell backs them), while sending
   * the current value is a no-op (the API skips identical values, preserving the AI flag).
   */
  getValuesForLanguage: (lang: LanguageCode) => Record<string, string>;
  /**
   * Clears the modified-tracking of a language after its cells were successfully saved. Without
   * this, the next save re-sends the same fields as HUMAN edits — re-stamping a cell the user may
   * have handed back to the AI in between.
   */
  markLanguageSaved: (lang: LanguageCode) => void;
  isFieldStale: (field: string) => boolean;
  /** True when the non-primary language is a valid translation target for this entity. */
  canAutoTranslate: boolean;
  /** True when a field's non-primary translation is managed by the AI (no human-authored cell). */
  isFieldAiManaged: (field: string) => boolean;
  /**
   * True when the field can be handed back to the AI in the currently displayed language: that
   * language is human-authored AND another language still holds a human source to regenerate from.
   * Factual criterion that drives the toggle — never shown on the only source.
   */
  isFieldHandBackable: (field: string) => boolean;
  /** True while a field is being handed back to the AI (regeneration in flight). */
  isFieldRegenerating: (field: string) => boolean;
  /** Hand a field back to the AI (drops the human translation, re-triggers auto-translation). */
  enableAiForField: (field: string) => void;
  /** True if the field has non-empty content in ANY language (for "filled in at least one language" checks). */
  hasValueInAnyLanguage: (field: string) => boolean;
  /**
   * True once the non-primary translations query has resolved. Uncontrolled inputs (TextField
   * defaultValue, Tiptap) only read their value on mount: feed this to
   * TranslatableFieldWrapper's contentReadyToken so they remount once when the data lands —
   * otherwise switching language BEFORE the query resolves shows empty inputs forever (and typing
   * over that invisible content would overwrite it on save).
   */
  translationsLoaded: boolean;
  /**
   * Per-field remount token for TranslatableFieldWrapper's contentReadyToken: combines the
   * initial data-readiness with a version bumped whenever a background refetch changes the
   * UNTOUCHED field's value (fresh worker translation arriving after a save) — the uncontrolled
   * input then remounts and displays it live, without a page reload. A field being edited never
   * remounts (modified fields are never merged over).
   */
  getFieldContentToken: (field: string) => string;
  /**
   * The language whose cells are the hand-written source. Explicit sourceLanguage (the talent's
   * PreferredLanguage) when provided; otherwise the backend primary (Fr) — e.g. CompanyForm,
   * where the CSM authors the primary and the talent-specific rules don't apply.
   */
  sourceLanguage: LanguageCode;
  /** UI state of the field in the DISPLAYED language (drives the wrapper's status chip). */
  getFieldUiState: (field: string) => TranslatableFieldUiState;
  /**
   * True when the field must be locked in the DISPLAYED language: the translation is AI-managed
   * ("ai" state) and the user has not explicitly unlocked it (draft). Same condition as the
   * wrapper's visual lock — but consumers must carry it on the input's NATIVE read-only mode
   * (TextField InputProps.readOnly, Tiptap editable={false}). A pointer-events:none tarp on the
   * container also killed the editor's internal scrolling, text selection/copy and assistive-tech
   * access on long content; native read-only prevents edits while keeping all of those alive.
   */
  isFieldReadOnly: (field: string) => boolean;
  /**
   * AI-first gate: false while the NON-SOURCE language of the field has nothing to show — no
   * generated cell yet (target languages), and no pre-existing human version (non-target
   * languages, which are otherwise never surfaced: nothing will ever be generated for them).
   * Always true without an explicit sourceLanguage (CSM/manual contexts); always false for a
   * not-yet-persisted entity (creation is source-only).
   */
  isLanguageSelectorVisible: (field: string) => boolean;
  /**
   * Whether the "the X version will be generated after saving" note should show: the selector is
   * hidden AND the non-source language really is an auto-translation target (the note is a
   * promise — it must never show for a non-target language, where nothing will ever come).
   */
  isAiFirstNoteVisible: (field: string) => boolean;
  /** True when the field is in explicit unlock (draft) mode for the displayed language. */
  isFieldDraft: (field: string) => boolean;
  /**
   * Unlocks an AI-managed field — the ONLY act of takeover. From then on typing edits the value
   * and stamps the field as modified like any other field: the STEP's real save button is what
   * persists it (there is deliberately no field-level "save my version" — a local-only save
   * would read as a real one). The pre-unlock value is kept as a backup for cancel.
   */
  beginFieldDraft: (field: string) => void;
  /**
   * Discards the unlock without a trace: restores the pre-unlock value, un-stamps the field and
   * locks it again. Returns the restored value so callers can resync side channels (e.g. formik
   * holding the FR mirror).
   */
  cancelFieldDraft: (field: string) => string;
}

/**
 * Hook to manage multi-language form state for translatable fields.
 *
 * - FR values come from the entity itself (primary language)
 * - Other language values are loaded from the translations API
 * - Tracks which languages were modified for efficient saving
 */
export const useTranslatableForm = ({
  entityType,
  entityId,
  translatableFields,
  initialValues,
  sourceLanguage,
  enabled = true,
}: UseTranslatableFormOptions): UseTranslatableFormReturn => {
  const { currentLanguage } = useLanguage();
  const { showError } = useSnackbarContext();

  // The source = the talent's working language; fall back to the UI language. Normalise the
  // casing: a lowercase "fr"/"en" leaking in here (e.g. before the user's preference resolved on
  // a hard reload) would make editingLanguage miss every `values["Fr"|"En"]` key → empty fields.
  const sourceLang = toLanguageCode(sourceLanguage ?? currentLanguage);

  // Whether the caller anchored the source explicitly (talent screens pass the talent's
  // PreferredLanguage). Without it (CompanyForm — CSM-managed), the talent-specific rules
  // (AI-first gate) don't apply and the "source" role falls back to the backend primary (Fr),
  // which is what the CSM authors.
  const hasExplicitSource = sourceLanguage !== undefined;
  const stateSourceLang = hasExplicitSource ? sourceLang : PRIMARY_LANGUAGE;

  // Editing starts on the source language (what the talent authors in).
  const [editingLanguage, setEditingLanguage] = useState<LanguageCode>(sourceLang);

  // Values per language per field: { Fr: { description: "...", market: "..." }, En: { ... } }
  // The primary (Fr) is seeded from the entity columns ONLY when Fr IS the source language.
  // For an EN-source talent those columns are a MACHINE projection, only trustworthy once the
  // worker has run: seeding them would resurrect whatever they hold (e.g. the English source on
  // a not-yet-projected or legacy profile) in the FR tab. Their FR must come exclusively from
  // the Translation cell (the primary merge below).
  const [values, setValues] = useState<Record<string, Record<string, string>>>(() => {
    if (stateSourceLang !== PRIMARY_LANGUAGE) {
      return { [PRIMARY_LANGUAGE]: {} };
    }
    const frValues: Record<string, string> = {};
    translatableFields.forEach((field) => {
      frValues[field] = initialValues[field] ?? "";
    });
    return { [PRIMARY_LANGUAGE]: frValues };
  });

  // Track which languages have been modified, and which (language, field) pairs specifically.
  // Per-field tracking ensures a save only writes the fields the user actually touched — otherwise
  // saving one field in a language would rewrite ALL of that language's cells as human-authored,
  // clobbering the AI flag on the untouched fields.
  const modifiedLanguages = useRef<Set<string>>(new Set());
  const modifiedFields = useRef<Set<string>>(new Set());

  // (language::field) pairs the user explicitly took over THIS SESSION (draft committed). Unlike
  // modifiedFields, this survives markLanguageSaved: after a save the tracking is purged but the
  // server flags may not have refetched yet — without this the field would briefly flip back to
  // the locked "ai" state. Cleared only when the field is handed back to the AI.
  const sessionHumanFields = useRef<Set<string>>(new Set());

  // Explicit unlocks: the map holds, per unlocked field, the language and the PRE-UNLOCK value
  // (the backup "cancel" restores). Typing after the unlock edits `values` and stamps the field
  // as modified normally — the unlock click itself is the deliberate takeover, and the step's
  // real save button is the only save.
  const [draftFields, setDraftFields] = useState<
    Map<string, { language: LanguageCode; backup: string }>
  >(new Map());
  const draftFieldsRef = useRef(draftFields);
  draftFieldsRef.current = draftFields;

  // Stable reference for translatableFields to avoid effect re-runs
  const fieldsRef = useRef(translatableFields);
  fieldsRef.current = translatableFields;

  // Render-time mirror of `values`: mergeIncomingTranslations must compare incoming data to the
  // current values WITHOUT depending on `values` (that would recreate the callback on every
  // keystroke and loop its effects).
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Bumped when a background merge changes an UNTOUCHED field's value (fresh worker translation
  // arriving after a save). Uncontrolled inputs (TextField defaultValue, Tiptap) only read their
  // value on mount, so the wrapper keys on this to remount the field and display the new text.
  // Modified fields are never merged over, so a field being edited never remounts.
  const [fieldContentVersions, setFieldContentVersions] = useState<Record<string, number>>({});

  // Sync FR values when initialValues change (entity loaded from API)
  const initialValuesKey = translatableFields
    .map((f) => `${f}:${initialValues[f] ?? ""}`)
    .join("|");

  useEffect(() => {
    // Same guard as the initial seed: an EN-source talent's FR lives ONLY in the Translation
    // cell — reseeding from the entity columns (e.g. after the profile refetch that follows a
    // step save) would resurrect their machine-projected/legacy content in the FR tab during
    // the worker window. The closure runs with the render where initialValues arrived, so
    // stateSourceLang is the one resolved from the SAME loaded profile.
    if (stateSourceLang !== PRIMARY_LANGUAGE) return;
    const fields = fieldsRef.current;
    const frValues: Record<string, string> = {};
    fields.forEach((field) => {
      frValues[field] = initialValues[field] ?? "";
    });
    setValues((prev) => ({
      ...prev,
      [PRIMARY_LANGUAGE]: frValues,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesKey, stateSourceLang]);

  // Fetch translations for non-primary languages (En for now)
  const fetchLang = LanguageCode.En;

  // Fields handed back to the AI this session → the language being regenerated and the value shown
  // when the user opted in. The human cell is dropped server-side immediately and the worker
  // re-translates asynchronously, so we keep showing that previous value (greyed, "in progress") and
  // poll until the fresh AI value lands — for WHICHEVER language was handed back (FR or EN).
  // sawCellGone: a poll observed the deleted cell (empty value) — from then on ANY non-empty value
  // is the fresh AI output, even when the regenerated text happens to be identical to the previous
  // one (otherwise such a field would stay locked until the safety timeout).
  const [regeneratingFields, setRegeneratingFields] = useState<
    Map<string, { language: LanguageCode; previousValue: string; sawCellGone?: boolean }>
  >(new Map());
  // Poll while at least one field is awaiting its AI re-translation; stops as soon as they all land.
  const pollInterval = regeneratingFields.size > 0 ? 3000 : false;

  // Safety-net timers per field, tracked so they can be cancelled: a stale timer from a previous
  // hand-back must not kill a newer in-flight regeneration of the same field, and pending timers
  // must not fire after unmount.
  const regenTimersRef = useRef<Map<string, number>>(new Map());
  const clearRegenTimer = useCallback((field: string) => {
    const timer = regenTimersRef.current.get(field);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      regenTimersRef.current.delete(field);
    }
  }, []);
  useEffect(() => {
    const timers = regenTimersRef.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const { data: translationsData } = useEntityTranslations(
    entityType,
    entityId,
    fetchLang.toLowerCase(),
    pollInterval,
    enabled
  );

  // Primary-language values too: handing the PRIMARY (FR) back to the AI must surface the fresh
  // translation live, without a reload. The entity columns mirror FR but are NOT re-fetched on
  // regenerate, so the Translation cell is the live source we poll here.
  const { data: primaryTranslationsData } = useEntityTranslations(
    entityType,
    entityId,
    PRIMARY_LANGUAGE.toLowerCase(),
    pollInterval,
    enabled
  );

  // Per-field flags (stale + human-managed) for the non-primary language.
  const { data: translationFlags } = useEntityTranslationFlags(
    entityType,
    entityId,
    fetchLang.toLowerCase(),
    pollInterval,
    enabled
  );

  // Flags for the primary-language cell too, so we know factually which languages are human-authored
  // (the toggle must only appear on a language that has ANOTHER human source to regenerate from).
  const { data: primaryTranslationFlags } = useEntityTranslationFlags(
    entityType,
    entityId,
    PRIMARY_LANGUAGE.toLowerCase(),
    pollInterval,
    enabled
  );

  // Whether the non-primary language is even a valid target (e.g. a talent who doesn't speak
  // English has no EN target → the auto-translate toggle must be hidden).
  const { data: targetLanguages } = useTargetLanguages(entityType, entityId, enabled);
  const canAutoTranslate = (targetLanguages ?? []).includes(fetchLang.toLowerCase());

  const regenerate = useRegenerateTranslation();

  // Merge freshly-fetched translation cells for ONE language into the form state. Shared by both
  // languages so handing EITHER back to the AI surfaces the new value live (no reload):
  //  - a field still being regenerated in this language keeps its greyed "in progress" value;
  //  - once the fresh AI value lands (cell back, non-empty, different) it is shown and the field
  //    leaves the regenerating set;
  //  - replaceUntouched=true (non-primary language, whose values live ONLY in the Translation table)
  //    syncs every other field from the server; for the primary language we keep the entity-sourced
  //    value instead, so a missing/empty cell never blanks the field.
  const mergeIncomingTranslations = useCallback(
    (lang: LanguageCode, data: Record<string, string> | undefined, replaceUntouched: boolean) => {
      if (!data) return;

      const fields = fieldsRef.current;

      const landed = new Set<string>();
      const cellGoneObserved = new Set<string>();
      regeneratingFields.forEach((info, field) => {
        if (info.language !== lang) return;
        const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
        const incoming = data[pascalField] ?? "";
        if (incoming === "") {
          // The deleted human cell was observed: the next non-empty value is the fresh AI output,
          // even if the regenerated text turns out identical to the previous one.
          if (!info.sawCellGone) cellGoneObserved.add(field);
          return;
        }
        if (incoming !== info.previousValue || info.sawCellGone) landed.add(field);
      });

      // A fresh worker translation reaching an UNTOUCHED field must also surface on SCREEN: bump
      // its content version so the wrapper remounts the uncontrolled input with the new value.
      // Applies to BOTH languages: the primary (FR) column is projected by the worker too (an
      // EN-source talent whose FR is generated), so a changed non-empty primary cell must show
      // live as well — not only the non-primary. Compared against the render-time mirror (not
      // `values` — dependency loop). Empty incoming never counts (the entity column is the source
      // for a missing primary cell; an emptied non-primary cell is handled by the merge below).
      const prevLangValues = valuesRef.current[lang] ?? {};
      const changedUntouched = fields.filter((field) => {
        const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
        const incoming = data[pascalField] ?? "";
        return (
          incoming !== "" &&
          incoming !== (prevLangValues[field] ?? "") &&
          !modifiedFields.current.has(`${lang}::${field}`) &&
          regeneratingFields.get(field)?.language !== lang
        );
      });
      if (changedUntouched.length > 0) {
        setFieldContentVersions((prev) => {
          const next = { ...prev };
          changedUntouched.forEach((field) => {
            next[field] = (next[field] ?? 0) + 1;
          });
          return next;
        });
      }

      setValues((prev) => {
        const prevLang = prev[lang] ?? {};
        const langValues: Record<string, string> = { ...prevLang };
        fields.forEach((field) => {
          const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
          const incoming = data[pascalField] ?? "";
          const reg = regeneratingFields.get(field);
          const isModified = modifiedFields.current.has(`${lang}::${field}`);
          if (reg?.language === lang && !landed.has(field)) {
            // Still re-translating this language: keep the greyed "in progress" value instead of
            // blanking it out (the human cell was dropped server-side).
            langValues[field] = prevLang[field] ?? reg.previousValue;
          } else if (landed.has(field)) {
            // Fresh AI translation landed → surface it live.
            langValues[field] = incoming;
          } else if (!isModified && replaceUntouched) {
            // Non-primary: the value lives ONLY in the Translation table → sync everything from
            // the server (including an empty cell = a genuine clear). Never overwrite a field the
            // user edited this session (the save reads this state).
            langValues[field] = incoming;
          } else if (!isModified && incoming !== "") {
            // Primary, untouched, with a non-empty changed cell (worker-projected FR): surface it.
            // An empty/absent primary cell keeps the entity-sourced value (the column is the source).
            langValues[field] = incoming;
          }
        });
        return { ...prev, [lang]: langValues };
      });

      if (landed.size > 0 || cellGoneObserved.size > 0) {
        landed.forEach(clearRegenTimer);
        setRegeneratingFields((prev) => {
          const next = new Map(prev);
          landed.forEach((field) => next.delete(field));
          cellGoneObserved.forEach((field) => {
            const info = next.get(field);
            if (info) next.set(field, { ...info, sawCellGone: true });
          });
          return next;
        });
      }
    },
    [regeneratingFields, clearRegenTimer]
  );

  // True once the non-primary data has been MERGED into the form state (not merely fetched):
  // the contentReadyToken remount must happen on the commit where values[En] are actually
  // available, or uncontrolled inputs (Tiptap reads `content` only at create) remount still empty
  // — one render too early defeats the whole token.
  const [nonPrimaryMerged, setNonPrimaryMerged] = useState(false);

  // Non-primary language: its values live only in the Translation table → full sync from the server.
  useEffect(() => {
    mergeIncomingTranslations(fetchLang, translationsData, true);
    if (translationsData !== undefined) {
      // Same effect as the merge → both state updates land in the same commit.
      setNonPrimaryMerged(true);
    }
  }, [translationsData, fetchLang, mergeIncomingTranslations]);

  // Primary language: only surface a freshly-regenerated cell; never overwrite the entity-sourced
  // value otherwise (the columns mirror FR and aren't re-fetched on regenerate).
  useEffect(() => {
    mergeIncomingTranslations(PRIMARY_LANGUAGE, primaryTranslationsData, false);
  }, [primaryTranslationsData, mergeIncomingTranslations]);

  const switchLanguage = useCallback((lang: LanguageCode) => {
    setEditingLanguage(toLanguageCode(lang));
  }, []);

  const getFieldValue = useCallback(
    (field: string): string => {
      return values[editingLanguage]?.[field] ?? "";
    },
    [values, editingLanguage]
  );

  const setFieldValue = useCallback(
    (field: string, value: string) => {
      modifiedLanguages.current.add(editingLanguage);
      modifiedFields.current.add(`${editingLanguage}::${field}`);
      setValues((prev) => ({
        ...prev,
        [editingLanguage]: {
          ...prev[editingLanguage],
          [field]: value,
        },
      }));
    },
    [editingLanguage]
  );

  const isFieldDraft = useCallback(
    (field: string): boolean => draftFields.get(field)?.language === editingLanguage,
    [draftFields, editingLanguage]
  );

  const beginFieldDraft = useCallback(
    (field: string) => {
      const current = valuesRef.current[editingLanguage]?.[field] ?? "";
      setDraftFields((prev) =>
        new Map(prev).set(field, { language: editingLanguage, backup: current })
      );
    },
    [editingLanguage]
  );

  const cancelFieldDraft = useCallback((field: string): string => {
    const draft = draftFieldsRef.current.get(field);
    if (!draft) return valuesRef.current[editingLanguage]?.[field] ?? "";
    // Restore the pre-unlock value and un-stamp the takeover: cancelling must leave no trace,
    // or the next step save would persist the discarded text as a human edit.
    setValues((prev) => ({
      ...prev,
      [draft.language]: {
        ...prev[draft.language],
        [field]: draft.backup,
      },
    }));
    modifiedFields.current.delete(`${draft.language}::${field}`);
    const languageStillModified = fieldsRef.current.some((f) =>
      modifiedFields.current.has(`${draft.language}::${f}`)
    );
    if (!languageStillModified) {
      modifiedLanguages.current.delete(draft.language);
    }
    setDraftFields((prev) => {
      const next = new Map(prev);
      next.delete(field);
      return next;
    });
    return draft.backup;
  }, [editingLanguage]);

  const getModifiedLanguages = useCallback(() => {
    const result: { languageCode: LanguageCode; fields: Record<string, string> }[] = [];
    modifiedLanguages.current.forEach((lang) => {
      const langCode = lang as LanguageCode;
      const langValues = values[langCode];
      if (!langValues) return;
      // Only include the fields the user actually edited in this language, so untouched
      // translations keep their existing cell (and AI flag) on save.
      const fields: Record<string, string> = {};
      Object.keys(langValues).forEach((field) => {
        if (modifiedFields.current.has(`${langCode}::${field}`)) {
          fields[field] = langValues[field];
        }
      });
      if (Object.keys(fields).length > 0) {
        result.push({ languageCode: langCode, fields });
      }
    });
    return result;
  }, [values]);

  const getPrimaryValues = useCallback(() => {
    return { ...values[PRIMARY_LANGUAGE] };
  }, [values]);

  const getValuesForLanguage = useCallback(
    (lang: LanguageCode) => {
      const result = { ...(values[lang] ?? {}) };
      // A field being handed back to the AI still SHOWS its previous human text (greyed), but its
      // cell was already deleted server-side. Save flows must send "" for it (the API skips empty
      // values on update), NOT the on-screen text — re-sending it would recreate a HUMAN cell and
      // silently undo the hand-back the user just confirmed.
      regeneratingFields.forEach((info, field) => {
        if (info.language === lang) {
          result[field] = "";
        }
      });
      return result;
    },
    [values, regeneratingFields]
  );

  const markLanguageSaved = useCallback((lang: LanguageCode) => {
    // A saved unlock is a completed takeover: stamp the field human for this session (the flags
    // refetch lags behind the save — without this the field would briefly re-lock as "ai") and
    // close the unlock. An unlocked-but-untouched field just re-locks: nothing was saved for it.
    // Read the refs SYNCHRONOUSLY, before the purge below — a setState updater runs at render
    // time, when modifiedFields would already be empty.
    draftFieldsRef.current.forEach((draft, field) => {
      if (draft.language !== lang) return;
      if (modifiedFields.current.has(`${lang}::${field}`)) {
        sessionHumanFields.current.add(`${lang}::${field}`);
      }
    });
    setDraftFields((prev) => {
      let changed = false;
      const next = new Map(prev);
      prev.forEach((draft, field) => {
        if (draft.language === lang) {
          next.delete(field);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    modifiedLanguages.current.delete(lang);
    fieldsRef.current.forEach((field) => {
      modifiedFields.current.delete(`${lang}::${field}`);
    });
  }, []);

  const hasValueInAnyLanguage = useCallback(
    (field: string): boolean =>
      Object.values(values).some((langValues) => {
        const v = langValues?.[field];
        return !!v && v.trim() !== "" && v !== "<p></p>";
      }),
    [values]
  );

  const isFieldStale = useCallback(
    (field: string): boolean => {
      // A human translation is out of sync in EITHER language → show the badge regardless of which
      // language is being viewed. Reading only the non-primary flags missed the case where the FR
      // human cell is stale (e.g. an EN-source talent edited their EN source).
      const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
      return (translationFlags?.staleFields.has(pascalField) ?? false)
        || (primaryTranslationFlags?.staleFields.has(pascalField) ?? false);
    },
    [translationFlags, primaryTranslationFlags]
  );

  const isFieldAiManaged = useCallback(
    (field: string): boolean => {
      // AI-managed = no human-authored cell for this field (absent, or an AI cell).
      const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
      return !(translationFlags?.humanManagedFields.has(pascalField) ?? false);
    },
    [translationFlags]
  );

  const isFieldHandBackable = useCallback(
    (field: string): boolean => {
      // NEVER on the source language: the talent's input language is hand-written by definition
      // and must not be replaceable by the machine (the API refuses it too — belt and braces).
      if (editingLanguage === stateSourceLang) return false;
      const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
      // Languages where this field is currently human-authored, and among them the LIVE ones
      // (non-stale): the API only accepts a hand-back when a NON-STALE human source remains in
      // another language (the dispatcher never regenerates from a stale cell).
      const humanLanguages = new Set<LanguageCode>();
      const liveHumanLanguages = new Set<LanguageCode>();
      if (primaryTranslationFlags?.humanManagedFields.has(pascalField)) {
        humanLanguages.add(PRIMARY_LANGUAGE);
        if (!primaryTranslationFlags.staleFields.has(pascalField)) {
          liveHumanLanguages.add(PRIMARY_LANGUAGE);
        }
      }
      if (translationFlags?.humanManagedFields.has(pascalField)) {
        humanLanguages.add(fetchLang);
        if (!translationFlags.staleFields.has(pascalField)) {
          liveHumanLanguages.add(fetchLang);
        }
      }
      // The API also refuses to regenerate a language that is not an auto-translation target for
      // this entity — don't offer a toggle that can only 400.
      const displayedIsTarget = (targetLanguages ?? []).includes(editingLanguage.toLowerCase());
      // Offer "hand back to AI" only when the displayed language is human (stale or not — handing
      // back a stale human translation is a prime use case), is a valid target, AND another LIVE
      // human source remains — so we never delete the only usable source.
      return (
        humanLanguages.has(editingLanguage) &&
        displayedIsTarget &&
        Array.from(liveHumanLanguages).some((lang) => lang !== editingLanguage)
      );
    },
    [primaryTranslationFlags, translationFlags, editingLanguage, targetLanguages, stateSourceLang]
  );

  const getFieldUiState = useCallback(
    (field: string): TranslatableFieldUiState => {
      // The source language is the talent's own text — never machine-managed, never locked.
      if (editingLanguage === stateSourceLang) return "source";
      const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
      const flags =
        editingLanguage === PRIMARY_LANGUAGE ? primaryTranslationFlags : translationFlags;
      const humanByFlags = flags?.humanManagedFields.has(pascalField) ?? false;
      const key = `${editingLanguage}::${field}`;
      // A field edited or explicitly taken over this session displays as human even before the
      // server flags confirm it (save + refetch lag) — it must not re-lock under the user.
      const humanThisSession =
        modifiedFields.current.has(key) || sessionHumanFields.current.has(key);
      if (!humanByFlags && !humanThisSession) return "ai";
      return humanByFlags && (flags?.staleFields.has(pascalField) ?? false)
        ? "humanStale"
        : "human";
    },
    [editingLanguage, stateSourceLang, primaryTranslationFlags, translationFlags]
  );

  const isFieldReadOnly = useCallback(
    // Reuses getFieldUiState/isFieldDraft (single source of truth — the exact condition of the
    // wrapper's "ai" lock): AI-managed in the displayed language AND not explicitly unlocked.
    // Consumers feed this to the input's NATIVE read-only mode so the lock no longer needs a
    // pointer-events:none tarp (which broke Tiptap's internal scroll, selection/copy and a11y).
    (field: string): boolean => getFieldUiState(field) === "ai" && !isFieldDraft(field),
    [getFieldUiState, isFieldDraft]
  );

  const isLanguageSelectorVisible = useCallback(
    (field: string): boolean => {
      // AI-first applies wherever a source is anchored (talent fields, Company anchored to the
      // platform primary). Without an explicit source, or when the queries are gated off, the
      // selector stays visible so manual authoring in both languages remains possible.
      if (!hasExplicitSource || !enabled) return true;
      // Creation: the entity does not exist yet, so no translation can have been generated —
      // AI-first means only the source language is writable. The other language appears once
      // the first save has dispatched and the worker delivered (same rule as everywhere else;
      // returning true here would reopen the "write a translation before the AI" door on every
      // new company / new experience).
      if (!entityId) return false;
      const nonSourceLang =
        stateSourceLang === PRIMARY_LANGUAGE ? fetchLang : PRIMARY_LANGUAGE;
      // Target list still loading: default hidden — flashing the selector at a talent whose
      // translation was never generated would invite a manual write the AI-first rule forbids.
      if (targetLanguages === undefined) return false;
      // Show the selector only when the non-source language has something to show: the generated
      // cell once it lands (target languages), OR a pre-existing human version (legacy data of a
      // talent who no longer targets that language — hiding it would orphan their own text). A
      // NON-TARGET language with no content stays hidden: it will never be generated, and showing
      // it dressed the "never-translated" case as an "✦ automatic translation" that was a lie.
      return hasMeaningfulContent(values[nonSourceLang]?.[field]);
    },
    [hasExplicitSource, enabled, entityId, stateSourceLang, fetchLang, targetLanguages, values]
  );

  const isAiFirstNoteVisible = useCallback(
    (field: string): boolean => {
      // The "will be generated after saving" note must only make a promise the worker will keep:
      // selector hidden AND the non-source language actually is an auto-translation target. On a
      // non-target language (or while targets are unknown — creation) the note would lie.
      if (isLanguageSelectorVisible(field)) return false;
      if (!hasExplicitSource || !enabled || !entityId) return false;
      const nonSourceLang =
        stateSourceLang === PRIMARY_LANGUAGE ? fetchLang : PRIMARY_LANGUAGE;
      return targetLanguages?.includes(nonSourceLang.toLowerCase()) ?? false;
    },
    [isLanguageSelectorVisible, hasExplicitSource, enabled, entityId, stateSourceLang, fetchLang, targetLanguages]
  );

  const isFieldRegenerating = useCallback(
    // Only show the "in progress" state on the language actually being handed back to the AI.
    (field: string): boolean => regeneratingFields.get(field)?.language === editingLanguage,
    [regeneratingFields, editingLanguage]
  );

  const getFieldContentToken = useCallback(
    (field: string): string => `${nonPrimaryMerged}-${fieldContentVersions[field] ?? 0}`,
    [nonPrimaryMerged, fieldContentVersions]
  );

  const enableAiForField = useCallback(
    (field: string) => {
      if (!entityId) return;
      const pascalField = field.charAt(0).toUpperCase() + field.slice(1);
      // Act on the DISPLAYED language: handing back the FR regenerates it from EN, and vice versa.
      const targetLanguage = editingLanguage;
      // The user just handed this field to the AI: drop it from the modified-tracking, or the next
      // save would re-send the on-screen text as a HUMAN edit and silently undo the hand-back
      // (a non-primary human write re-stamps the cell and the AI stops maintaining it).
      modifiedFields.current.delete(`${targetLanguage}::${field}`);
      // The session takeover is over too — the field is machine-managed again.
      sessionHumanFields.current.delete(`${targetLanguage}::${field}`);
      // Any open draft for this field is moot (the cell it was based on is being regenerated).
      setDraftFields((prev) => {
        if (!prev.has(field)) return prev;
        const next = new Map(prev);
        next.delete(field);
        return next;
      });
      const languageStillModified = fieldsRef.current.some((f) =>
        modifiedFields.current.has(`${targetLanguage}::${f}`)
      );
      if (!languageStillModified) {
        modifiedLanguages.current.delete(targetLanguage);
      }
      // Remember the value currently on screen so we can (a) keep showing it while the worker
      // re-translates and (b) detect when the fresh AI value has landed (it will differ).
      const currentValue = values[targetLanguage]?.[field] ?? "";
      // A previous run's safety-net timer must not be allowed to kill this new regeneration.
      clearRegenTimer(field);
      setRegeneratingFields((prev) =>
        new Map(prev).set(field, { language: targetLanguage, previousValue: currentValue })
      );
      const stopRegenerating = () => {
        clearRegenTimer(field);
        setRegeneratingFields((prev) => {
          if (!prev.has(field)) return prev;
          const next = new Map(prev);
          next.delete(field);
          return next;
        });
      };
      // Safety net: stop greying after 60s even if the AI value never lands (worker error).
      regenTimersRef.current.set(field, window.setTimeout(stopRegenerating, 60000));
      regenerate.mutate(
        {
          entityType,
          entityId,
          propertyName: pascalField,
          languageCode: targetLanguage.toLowerCase(),
        },
        {
          // The server can refuse (last live human source, language not a target, queueing failed
          // with the cell restored): stop the spinner AND tell the user — they just confirmed a
          // "your text will be replaced" dialog, a silent no-op reads as data loss.
          onError: () => {
            stopRegenerating();
            showError("translations.autoTranslateError");
          },
        }
      );
    },
    [entityId, entityType, regenerate, editingLanguage, values, clearRegenTimer, showError]
  );

  return {
    editingLanguage,
    switchLanguage,
    getFieldValue,
    setFieldValue,
    getModifiedLanguages,
    getPrimaryValues,
    getValuesForLanguage,
    markLanguageSaved,
    isFieldStale,
    canAutoTranslate,
    isFieldAiManaged,
    isFieldHandBackable,
    isFieldRegenerating,
    enableAiForField,
    hasValueInAnyLanguage,
    translationsLoaded: nonPrimaryMerged,
    getFieldContentToken,
    sourceLanguage: stateSourceLang,
    getFieldUiState,
    isFieldReadOnly,
    isLanguageSelectorVisible,
    isAiFirstNoteVisible,
    isFieldDraft,
    beginFieldDraft,
    cancelFieldDraft,
  };
};
